import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useForm, type Resolver } from 'react-hook-form';
import { type QueryClient } from '@tanstack/react-query';

import { FixtureTypeDefaultUnit } from '#apps/client-web/features/audit-form-water-fixture/constants';
import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { useSaveAuditFormMutation } from '#apps/client-web/features/audit-form/hooks/use-save-audit-form-mutation';
import { AUDIT_FORMS_QUERY_KEY } from '#apps/client-web/features/audit-form/types/react-query';
import { useSaveFileMutation } from '#apps/client-web/features/file-storage/hooks/use-save-file-mutation';
import {
  type FileContent,
  type FileStorageService,
} from '#apps/client-web/features/file-storage/services/file-storage';
import { useLoadFilesQuery } from '#apps/client-web/features/file-storage/hooks/use-load-file-query';
import { useDeleteFileMutation } from '#apps/client-web/features/file-storage/hooks/use-delete-file-mutation';

import { isNil } from '#packages/data-collection/libs/lodash';
import { type AuditId } from '#packages/data-collection/models/audit';
import {
  AuditWaterFixtureFormSchema,
  type AuditWaterFixtureForm,
  type AuditWaterFixtureFormEntry,
  type FixtureType,
} from '#packages/data-collection/models/audit-water-fixture-form';
import { type Attachment } from '#packages/data-collection/models/common/attachment';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { useMenu } from '#packages/web/hooks/use-menu';

import { fromFormData, toFormData } from './mappers';
import {
  EditAuditFormWaterFixtureEntryDataSchema,
  type EditAuditFormWaterFixtureEntryData,
} from './types';
import { zodResolver } from '@hookform/resolvers/zod';

export type AuditFormWaterFixtureEntryPageViewModel = ReturnType<
  typeof useAuditFormWaterFixtureEntryPageViewModel
>;

type AuditFormWaterFixtureEntryPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  queryClient: QueryClient;
};

export const useAuditFormWaterFixtureEntryPageViewModel = ({
  logger,
  auditFormService,
  fileStorageService,
  queryClient,
}: AuditFormWaterFixtureEntryPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });

  const { auditId, index: indexAsString } = useParams<{
    auditId: AuditId;
    index: string;
  }>();
  const index = parseInt(indexAsString!);

  const auditFormQuery = useLoadAuditFormByType<AuditWaterFixtureForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'water-fixture',
    queryClient,
  });

  const saveFileStorageMutation = useSaveFileMutation({
    fileStorageService,
    queryClient,
  });

  const deleteFileStorageMutation = useDeleteFileMutation({
    fileStorageService,
    queryClient,
  });

  const fileIds = useMemo(
    () =>
      (auditFormQuery.data?.entries[index]?.attachments ?? []).map(
        (item) => item.name,
      ),
    [auditFormQuery.data?.entries],
  );

  const fileStorageQuery = useLoadFilesQuery({
    fileStorageService,
    queryClient,
    fileIds,
  });

  const saveAuditFormMutation = useSaveAuditFormMutation<AuditWaterFixtureForm>(
    {
      auditFormService,
      queryClient,
      auditId: auditId!,
      formType: 'water-fixture',
    },
  );

  const form = useForm({
    resolver: zodResolver(
      EditAuditFormWaterFixtureEntryDataSchema,
    ) as Resolver<EditAuditFormWaterFixtureEntryData>,
    reValidateMode: 'onChange',

    defaultValues: {},
  });

  const onSave = useCallback(
    (formData: EditAuditFormWaterFixtureEntryData) => {
      if (loadAuditFormState.state !== 'success') {
        logger?.debug(
          'Cannot update water fixture entry, page is not in success state',
        );
        return;
      }

      logger?.debug({ auditId, formData }, 'Updating water fixture form data');

      const newFileIds = (formData.attachments ?? []).map(
        (attachment) => attachment.name,
      );
      const deletedFileIds = fileIds.filter(
        (fileId) => !newFileIds.includes(fileId),
      );
      deletedFileIds.forEach((fileId) => {
        deleteFileStorageMutation.mutate({
          fileId,
        });
      });

      const attachments: Attachment[] | undefined = formData.attachments?.map(
        (attachment) => {
          saveFileStorageMutation.mutate({
            fileId: attachment.name,
            content: attachment.file,
          });
          return {
            name: attachment.name,
            mimeType: attachment.file.type,
            isSelectedForReport: attachment.isSelectedForReport,
          };
        },
      );

      // Encode existing data to raw format, or start with empty entries
      const previousDataEncoded = auditFormQuery.data
        ? AuditWaterFixtureFormSchema.encode(auditFormQuery.data)
        : { type: 'water-fixture' as const, entries: [] };

      const newEntryRaw = fromFormData(formData, attachments);

      if (index === -1) {
        previousDataEncoded.entries.push(newEntryRaw);
      } else {
        previousDataEncoded.entries[index] = newEntryRaw;
      }

      logger?.debug({ entry: previousDataEncoded.entries[index] });

      // Decode once to validate and transform
      const auditWaterFixtureForm =
        AuditWaterFixtureFormSchema.decode(previousDataEncoded);

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: auditWaterFixtureForm },
        {
          onSuccess: (savedForm: AuditWaterFixtureForm) => {
            logger?.debug(
              { auditId, savedForm },
              'WaterFixture form updated successfully',
            );
            // invalidate audit water fixture form queries
            queryClient
              .invalidateQueries({
                queryKey: [
                  AUDIT_FORMS_QUERY_KEY,
                  auditId,
                  previousDataEncoded.type,
                ],
              })
              .then(() => {
                logger?.debug('WaterFixture form queries invalidated');
                goBack();
              });
          },
          onError: (err) => {
            logger?.error({ err }, 'Failed to update water fixture form');
            throw err;
          },
        },
      );
    },
    [
      saveAuditFormMutation.mutate,
      index,
      auditId,
      saveFileStorageMutation.mutate,
      auditFormQuery.data,
      AuditWaterFixtureFormSchema.encode,
      AuditWaterFixtureFormSchema.decode,
    ],
  );

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (
      (saveAuditFormMutation.isIdle || saveAuditFormMutation.isSuccess) &&
      (saveFileStorageMutation.isIdle || saveFileStorageMutation.isSuccess) &&
      (deleteFileStorageMutation.isIdle || deleteFileStorageMutation.isSuccess)
    ) {
      return { state: 'idle' };
    }

    if (
      saveAuditFormMutation.isPending ||
      saveFileStorageMutation.isPending ||
      deleteFileStorageMutation.isPending
    ) {
      return { state: 'pending' };
    }

    if (saveAuditFormMutation.isError) {
      return { state: 'error', error: saveAuditFormMutation.error };
    }

    if (saveFileStorageMutation.isError) {
      return { state: 'error', error: saveFileStorageMutation.error };
    }

    if (deleteFileStorageMutation.isError) {
      return { state: 'error', error: deleteFileStorageMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    saveAuditFormMutation.isIdle,
    saveAuditFormMutation.isPending,
    saveAuditFormMutation.isSuccess,
    saveAuditFormMutation.isError,
    saveAuditFormMutation.error,
    saveFileStorageMutation.isIdle,
    saveFileStorageMutation.isPending,
    saveFileStorageMutation.isSuccess,
    saveFileStorageMutation.isError,
    saveFileStorageMutation.error,
    deleteFileStorageMutation.isIdle,
    deleteFileStorageMutation.isPending,
    deleteFileStorageMutation.isSuccess,
    deleteFileStorageMutation.isError,
    deleteFileStorageMutation.error,
  ]);

  const loadAuditFormState = useMemo<
    QueryPageState<{
      form: AuditWaterFixtureFormEntry | undefined;
      files: (FileContent | undefined)[];
    }>
  >(() => {
    const areFilesLoaded =
      fileIds.length === 0 ||
      fileStorageQuery.every((query) => query.isSuccess);
    if (auditFormQuery.isSuccess && areFilesLoaded) {
      const files =
        fileStorageQuery.map((item) => item.data ?? undefined) ?? undefined;
      return {
        state: 'success',
        data: { form: auditFormQuery.data?.entries[index], files },
      };
    }

    const areFilesLoading =
      fileIds.length > 0 &&
      fileStorageQuery.some((query) => query.isLoading || query.isPending);
    if (
      auditFormQuery.isLoading ||
      auditFormQuery.isPending ||
      areFilesLoading
    ) {
      return { state: 'loading' };
    }

    if (auditFormQuery.isError) {
      return {
        state: 'error',
        error: auditFormQuery.error,
      };
    }

    const fileError = fileStorageQuery.find((query) => query.isError)?.error;
    if (!isNil(fileError)) {
      return {
        state: 'error',
        error: fileError,
      };
    }

    throw new Error('Unhandled query state');
  }, [
    auditFormQuery.data,
    auditFormQuery.isSuccess,
    auditFormQuery.isLoading,
    auditFormQuery.isPending,
    auditFormQuery.isError,
    auditFormQuery.error,
    fileStorageQuery,
    fileIds,
    index,
  ]);

  const mainMenu = useMenu();

  const deleteDialog = useDialog<AuditWaterFixtureFormEntry>();
  const isDeleteDisabled =
    updateMutationState.state === 'pending' ||
    loadAuditFormState.state !== 'success' ||
    loadAuditFormState.data.form == null ||
    index < 0;
  const onDelete = useCallback(
    ({ confirmed }: { confirmed: boolean }) => {
      if (isDeleteDisabled || isNil(auditFormQuery.data)) {
        logger?.debug(
          'Cannot delete water fixture entry, page is not in success state for an existing entry',
        );
        return;
      }

      mainMenu.close();

      // show confirmation dialog
      if (!confirmed) {
        logger?.debug(
          'Opening water fixture entry deletion confirmation dialog',
        );
        deleteDialog.open(loadAuditFormState.data.form);
        return;
      }

      const updatedData = { ...auditFormQuery.data };
      updatedData.entries.splice(index, 1);

      // perform deletion
      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: updatedData },
        {
          onSuccess: (auditForm) => {
            logger?.debug(
              { auditId, auditForm },
              'WaterFixture entry deleted successfully',
            );

            // invalidate audit list queries
            queryClient
              .invalidateQueries({
                queryKey: [AUDIT_FORMS_QUERY_KEY, auditId, updatedData.type],
              })
              .then(() => {
                logger?.debug('WaterFixture list queries invalidated');
                goBack();
              })
              .catch((err) => {
                logger?.error(
                  { err },
                  'Failed to invalidate water fixture list queries',
                );
                throw err;
              });
          },

          onError: (err) => {
            logger?.error(
              { err, auditId },
              'Failed to delete water fixture entry',
            );

            throw err;
          },
        },
      );
    },
    [auditId, loadAuditFormState, saveAuditFormMutation],
  );

  const changeFixtureType = useCallback(
    (fixtureType: FixtureType) => {
      console.log('fixtype', fixtureType);
      form.setValue('fixtureType', fixtureType);
      form.setValue('unit', FixtureTypeDefaultUnit[fixtureType]);
    },
    [form.setValue],
  );

  // sync form default values when audit details are loaded
  useEffect(() => {
    if (loadAuditFormState.state !== 'success') {
      return;
    }

    logger?.debug(
      { auditWaterFixtureForm: auditFormQuery.data },
      'Audit water fixture form default values',
    );

    // update form default values
    form.reset(
      toFormData(loadAuditFormState.data.form, loadAuditFormState.data.files),
    );
  }, [auditFormQuery.data?.entries, auditFormQuery.isSuccess]);

  return {
    mainMenu,

    auditFormQuery,
    loadAuditFormState,

    form,
    onSubmit: form.handleSubmit(onSave),
    updateMutationState,

    onDelete,
    deleteDialog,
    isDeleteDisabled,

    changeFixtureType,

    auditId,
  };
};
